import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";

import toast from "react-hot-toast";

import { getTenant } from "../../../utils/tenantManager";

import {
  createCalibrationCertificateApi,
  deleteCalibrationCertificateApi,
  getCalibrationCertificateByIdApi,
  getCalibrationCertificatesApi,
  updateCalibrationCertificateApi,
  getCalibrationCertificatesByIdsApi,
} from "../../../services/operation/calibrationcertificate/calibrationCertificateService";

export const useCalibrationCertificateViewModel = () => {
  const tenantId = getTenant();

  const [searchParams] = useSearchParams();

  const [calibrationCertificates, setCalibrationCertificates] = useState<any[]>(
    [],
  );

  const [loading, setLoading] = useState(false);

  const [page, setPage] = useState(() =>
    Number(searchParams.get("page") ?? "1"),
  );

  const [search, setSearch] = useState(() => searchParams.get("search") ?? "");

  const [totalCount, setTotalCount] = useState(0);

  // ================= LOAD =================
  const loadCalibrationCertificates = async () => {
    try {
      setLoading(true);

      const res = await getCalibrationCertificatesApi(
        page,
        10,
        search,
        tenantId!,
      );

      setCalibrationCertificates(res?.data?.data || []);

      setTotalCount(res?.data?.totalCount || 0);
    } catch {
      toast.error("Failed to load certificates");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCalibrationCertificates();
  }, [page, search]);

  useEffect(() => {
    const params = new URLSearchParams();

    params.set("page", page.toString());

    if (search) {
      params.set("search", search);
    }

    window.history.replaceState(
      {},
      "",
      `${window.location.pathname}?${params.toString()}`,
    );
  }, [page, search]);

  // ================= GET ONE =================
  const getCalibrationCertificateById = async (id: string) => {
    try {
      const res = await getCalibrationCertificateByIdApi(id, tenantId!);

      return res?.data;
    } catch {
      toast.error("Failed to load certificate");
    }
  };

  // ================= CREATE =================
  const createCalibrationCertificate = async (data: any) => {
    try {
      const payload = {
        ...data,

        lineItems: (data.lineItems || []).map((item: any, index: number) => ({
          ...item,
          lineNumber: index + 1,
        })),
      };

      console.log("CREATE LineItems:", payload.lineItems);

      const res = await createCalibrationCertificateApi(payload, tenantId!);

      if (res?.success) {
        toast.success("Certificate created");

        loadCalibrationCertificates();

        return true;
      }

      return false;
    } catch (err: any) {
      console.error("CREATE CALIBRATION ERROR:", err);

      console.error("STATUS:", err?.response?.status);

      console.error("RESPONSE:", JSON.stringify(err?.response?.data, null, 2));

      console.error(
        "VALIDATION ERRORS:",
        JSON.stringify(err?.response?.data?.errors, null, 2),
      );

      toast.error(
        err?.response?.data?.title ||
          err?.response?.data?.message ||
          "Create failed",
      );

      return false;
    }
  };

  // ================= UPDATE =================
  const updateCalibrationCertificate = async (data: any) => {
    try {
      const payload = {
        ...data,

        lineItems: (data.lineItems || []).map((item: any, index: number) => ({
          ...item,

          // Always regenerate the sequence
          lineNumber: index + 1,
        })),
      };

      console.log("UPDATE LineItems:", payload.lineItems);

      const res = await updateCalibrationCertificateApi(payload, tenantId!);

      if (res?.success) {
        toast.success("Certificate updated");

        loadCalibrationCertificates();

        return true;
      }

      return false;
    } catch (err: any) {
      console.error("UPDATE CALIBRATION ERROR:", err);
      console.error("STATUS:", err?.response?.status);

      console.error("RESPONSE:", JSON.stringify(err?.response?.data, null, 2));

      console.error(
        "VALIDATION ERRORS:",
        JSON.stringify(err?.response?.data?.errors, null, 2),
      );

      toast.error(
        err?.response?.data?.title ||
          err?.response?.data?.message ||
          "Create failed",
      );

      return false;
    }
  };

  // ================= DELETE =================
  const deleteCalibrationCertificate = async (id: string) => {
    try {
      const res = await deleteCalibrationCertificateApi(id, tenantId!);

      if (res?.success) {
        loadCalibrationCertificates();
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Delete failed");
    }
  };

  const getCalibrationCertificatesByIds = async (calibrationIds: string[]) => {
    try {
      setLoading(true);

      const response = await getCalibrationCertificatesByIdsApi(
        calibrationIds,
        tenantId!,
      );

      return response.data;
    } catch (error) {
      console.error(error);
      return [];
    } finally {
      setLoading(false);
    }
  };

  return {
    calibrationCertificates,
    loading,

    page,
    setPage,

    search,
    setSearch,

    totalCount,

    createCalibrationCertificate,

    getCalibrationCertificateById,

    updateCalibrationCertificate,

    deleteCalibrationCertificate,

    getCalibrationCertificatesByIds,
  };
};
